import * as React from 'react'

import type { DataTableSource } from 'generated/resources/datatable.graphql'
import once from 'lodash/once'

import { SORT_DIRECTION } from 'modules/core/components/DataTable/constants'
import type { SortDirection, SortType } from 'modules/core/components/DataTable/types'

type Context<S extends string> = {
  sortBy: S | undefined
  changeSortBy: (arg: S) => void
  sortOrder: SortDirection
  toggleOrder: () => void
  getSortVariables: () => { sortOrder: { field: S; order: SortDirection } } | undefined
}

type Props<T, S extends string> = {
  children: React.ReactNode
  source: DataTableSource<T, S>
  sort: SortType<S>
  onSortCallback?: (sort: SortType<S>) => void
}

function getKey(tableIdentifier: string) {
  return `datatable-sort-${tableIdentifier}`
}

function getPersistedSort<SourceFields extends string>(
  tableIdentifier: string,
  availableSorts: SourceFields[],
  defaultSort: SortType<SourceFields>
): SortType<SourceFields> {
  const key = getKey(tableIdentifier)
  const sortJSON = localStorage.getItem(key)
  const sort = sortJSON ? JSON.parse(sortJSON) : null

  if (
    sort &&
    availableSorts.includes(sort.path as SourceFields) &&
    Object.values(SORT_DIRECTION).includes(sort.direction)
  ) {
    return sort
  }

  return defaultSort
}

function saveSort<SourceFields extends string>(
  tableIdentifier: string,
  sort: SortType<SourceFields>
) {
  const key = getKey(tableIdentifier)

  localStorage.setItem(key, JSON.stringify(sort))
}

export const createSortContext = once(<S extends string>() =>
  React.createContext<Context<S> | null>(null)
)

export function SortProvider<T, S extends string>({
  children,
  source,
  sort: sortProp,
  onSortCallback,
}: Props<T, S>) {
  const Context = createSortContext<S>()
  const [sort, setSort] = React.useState<SortType<S>>(() =>
    getPersistedSort<S>(source.identifier, source.sort.fields, sortProp)
  )

  const { path, direction } = sort

  const toggleOrder = () => {
    const newSortOrder = direction === SORT_DIRECTION.ASC ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC
    const newSort = {
      ...sort,
      direction: newSortOrder,
    }

    onSortCallback?.(newSort)
    setSort(newSort)
    saveSort(source.identifier, newSort)
  }

  const changeSortBy = (arg: S) => {
    const newSort = {
      path: arg,
      direction: SORT_DIRECTION.ASC,
    }
    onSortCallback?.(newSort)
    setSort(newSort)
    saveSort(source.identifier, newSort)
  }

  function getSortVariables() {
    if (!path) {
      return undefined
    }

    return {
      sortOrder: {
        field: path,
        order: direction,
      },
    }
  }

  const value = {
    sortBy: path,
    sortOrder: direction,
    changeSortBy,
    toggleOrder,
    getSortVariables,
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}
